import React, {useCallback, useMemo} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Button} from '@vio/atlas/Button'
import {Flex} from '@vio/atlas/helpers/Flex'
import {Text} from '@vio/atlas/Text'

import {Colors, STANDARD_SHADES} from '../../constants'
import {Palettes} from '../../types'
import {SemanticPaletteSelector} from './SemanticPaletteSelector'

interface Props {
  colors: Colors
  paletteName: Palettes
  onChange?: (shade: string, color: string) => void
  baseColorPalettes?: string[]
  isSemanticPalette?: boolean
  onSourcePaletteChange?: (palette: string) => void
  selectedSourcePalette?: string
  onAutofillShades?: () => void
  onUndoAutofill?: () => void
  canUndoAutofill?: boolean
}

const ShadeLabel = styled.span<{mode: 'light' | 'dark'}>`
  position: absolute;
  bottom: 2px;
  right: 4px;
  font-size: 10px;
  color: ${({mode}) =>
    mode === 'light' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.4)'};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
`

const BaseColorBar = styled.div`
  width: 64px;
  height: 64px;
  position: relative;
  &:hover ${ShadeLabel} {
    opacity: 1;
  }
`

const ShadeBar = styled.div`
  width: 50px;
  height: 32px;
  position: relative;
  &:hover ${ShadeLabel} {
    opacity: 1;
  }
`

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: 64px repeat(5, 1fr);
  grid-template-rows: 32px 32px;
  grid-gap: 0;
  width: 100%;
  cursor: crosshair;
  &.semantic-palette {
    cursor: auto;
  }
`

const LighterShadesRow = styled.div`
  grid-row: 1;
  grid-column: span 1;
`

const DarkerShadesRow = styled.div`
  grid-row: 2;
  grid-column: span 1;
`

const BaseColorCell = styled.div`
  grid-column: 1;
  grid-row: 1 / span 2;
`

const MissingShadesLabel = styled.span`
  background: #ffd2d2;
  color: #b00020;
  font-size: 11px;
  border-radius: 4px;
  padding: 2px 6px;
  margin-left: 8px;
  font-weight: 500;
`

export const Palette = ({
  colors,
  paletteName,
  onChange,
  baseColorPalettes = [],
  isSemanticPalette = false,
  onSourcePaletteChange,
  selectedSourcePalette,
  onAutofillShades,
  onUndoAutofill,
  canUndoAutofill
}: Props) => {
  const {lighterShades, darkerShades, middleShade, middleColor} =
    useMemo(() => {
      const shadeKeys = Object.keys(colors).sort()
      const lighter = shadeKeys.filter(key => parseInt(key.slice(1)) < 500)
      const darker = shadeKeys.filter(key => parseInt(key.slice(1)) > 500)
      const middle = 'c500'

      return {
        lighterShades: lighter,
        darkerShades: darker,
        middleShade: middle,
        middleColor: colors[middle]
      }
    }, [colors])

  // Compute missing standard shades (only for base palettes)
  const missingShades = useMemo(() => {
    if (isSemanticPalette) return []
    return STANDARD_SHADES.filter(shade => !(shade in colors))
  }, [colors, isSemanticPalette])

  const handleColorClick = useCallback(
    (shade: string, event: React.MouseEvent<HTMLElement>) => {
      if (!onChange) return

      // Create a visible color input where the user clicked
      const input = document.createElement('input')
      input.type = 'color'
      input.value = colors[shade]

      const rect = event.currentTarget.getBoundingClientRect()
      input.style.position = 'fixed'
      input.style.left = `${rect.right + 5}px`
      input.style.top = `${rect.top}px`
      input.style.visibility = 'hidden'

      document.body.appendChild(input)

      // Update color immediately during picker interaction
      input.addEventListener('input', e => {
        const val = (e.target as HTMLInputElement).value
        onChange(shade, val)
      })

      // Clean up when done
      input.addEventListener('change', () => {
        document.body.removeChild(input)
      })

      input.addEventListener('blur', () => {
        setTimeout(() => {
          if (document.body.contains(input)) {
            document.body.removeChild(input)
          }
        }, 200)
      })

      // Open the picker
      setTimeout(() => input.click(), 50)
    },
    [colors, onChange]
  )

  const handleSourcePaletteChange = useCallback(
    (palette: string) => {
      if (onSourcePaletteChange) {
        onSourcePaletteChange(palette)
      }
    },
    [onSourcePaletteChange]
  )

  return (
    <Flex.Column gap="s200">
      <Flex.Row alignItems="center" gap="s100">
        <Text variant="titleM">{paletteName}</Text>
        {!isSemanticPalette && missingShades.length > 0 && (
          <MissingShadesLabel>
            Missing: {missingShades.join(', ')}
          </MissingShadesLabel>
        )}
      </Flex.Row>

      {/* Autofill/Undo buttons for base palettes only */}
      {!isSemanticPalette && (
        <Flex.Row gap="s100">
          {onAutofillShades && (
            <Button size="sm" variant="secondary" onClick={onAutofillShades}>
              Autofill Shades
            </Button>
          )}
          {onUndoAutofill && canUndoAutofill && (
            <Button size="sm" variant="secondary" onClick={onUndoAutofill}>
              Undo
            </Button>
          )}
        </Flex.Row>
      )}

      {isSemanticPalette &&
        baseColorPalettes.length > 0 &&
        onSourcePaletteChange && (
          <SemanticPaletteSelector
            selectedPalette={selectedSourcePalette || baseColorPalettes[0]}
            availablePalettes={baseColorPalettes}
            onChange={handleSourcePaletteChange}
          />
        )}

      <ColorGrid className={cx(isSemanticPalette && 'semantic-palette')}>
        <BaseColorCell>
          <BaseColorBar
            style={{
              backgroundColor: middleColor
            }}
            onClick={event => {
              handleColorClick(middleShade, event)
            }}
          >
            <ShadeLabel mode="dark">{middleShade}</ShadeLabel>
          </BaseColorBar>
        </BaseColorCell>

        {lighterShades.map(shade => (
          <LighterShadesRow key={shade}>
            <ShadeBar
              style={{
                backgroundColor: colors[shade]
              }}
              onClick={event => {
                handleColorClick(shade, event)
              }}
            >
              <ShadeLabel mode="dark">{shade}</ShadeLabel>
            </ShadeBar>
          </LighterShadesRow>
        ))}

        {darkerShades.map(shade => (
          <DarkerShadesRow key={shade}>
            <ShadeBar
              style={{
                backgroundColor: colors[shade]
              }}
              onClick={event => {
                handleColorClick(shade, event)
              }}
            >
              <ShadeLabel mode="light">{shade}</ShadeLabel>
            </ShadeBar>
          </DarkerShadesRow>
        ))}
      </ColorGrid>
    </Flex.Column>
  )
}
